<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Button,
		ButtonV2,
		Container,
		Modal,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system';
	import { capFirstChar } from 'src/helpers/utilities';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import LottiePlayer from 'src/components/LottiePlayer/LottiePlayer.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import HomesOnboardingStep from './HomesOnboardingStep.svelte';

	export let showModal = false;

	const dispatch = createEventDispatcher<{ close: void; complete: void }>();

	let currentStep = 0;
	let touchStartX = 0;
	let windowWidth = 0;

	$: isMobile = windowWidth <= SystemScreenBreakpoints.MOBILE_MAX;
	const HOMES_ONBOARDING_LOTTIE_ASPECT = 216 / 455;
	$: lottieWidth = isMobile ? 360 : 520;
	$: lottieHeight = Math.round(lottieWidth * HOMES_ONBOARDING_LOTTIE_ASPECT);
	const SWIPE_THRESHOLD = 50;

	const steps = [
		{
			heading: $format('homes_onboarding_step1_heading', {
				default: 'Multiple homes',
			}),
			description: $format('homes_onboarding_step1_description', {
				default:
					'Create and manage more than one home or location. Add homes from "Manage Homes" and switch between them to control devices in each place.',
			}),
			lottie: './images/lottie/homes/HomesOnboardingPart1.json',
			component: HomesOnboardingStep,
		},
		{
			heading: $format('homes_onboarding_step2_heading', {
				default: 'Transfer homes',
			}),
			description: $format('homes_onboarding_step2_description', {
				default:
					'Transfer ownership of a home to another Shelly account. For example  when ownership changes or when management needs to be transferred to another user. Use the transfer option on a home in your list.',
			}),
			lottie: './images/lottie/homes/HomesOnboardingPart2.json',
			component: HomesOnboardingStep,
		},
		{
			heading: $format('homes_onboarding_step4_heading', {
				default: 'Home sharing',
			}),
			description: $format('homes_onboarding_step4_description_home', {
				default:
					'Invite family members or guests to access a home without transferring ownership. Use "Invite to Home" to send an invitation by email.',
			}),
			lottie: './images/lottie/homes/HomesOnboardingPart4.json',
			component: HomesOnboardingStep,
		},
		{
			heading: $format('homes_onboarding_step5_heading', {
				default: 'Sharing permissions',
			}),
			description: $format('homes_onboarding_step5_description', {
				default:
					'Set what shared users can do: control devices, change settings, run or edit scenes, and access rooms. Choose full access or customize each permission when inviting or editing a guest.',
			}),
			lottie: './images/lottie/homes/HomesOnboardingPart4.json',
			component: HomesOnboardingStep,
		},
	];

	$: isFirstStep = currentStep === 0;
	$: isLastStep = currentStep === steps.length - 1;

	const goToStep = (idx: number) => {
		if (idx >= 0 && idx < steps.length) {
			currentStep = idx;
		}
	};

	const nextStep = () => {
		if (currentStep < steps.length - 1) {
			currentStep += 1;
		}
	};

	const prevStep = () => {
		if (currentStep > 0) {
			currentStep -= 1;
		}
	};

	const handleTouchStart = (e: TouchEvent) => {
		touchStartX = e.changedTouches[0].screenX;
	};

	const handleTouchEnd = (e: TouchEvent) => {
		if (window.innerWidth > SystemScreenBreakpoints.MOBILE_MAX) return;

		const diff = touchStartX - e.changedTouches[0].screenX;
		if (Math.abs(diff) < SWIPE_THRESHOLD) return;

		if (diff > 0 && !isLastStep) {
			currentStep += 1;
		} else if (diff < 0 && currentStep > 0) {
			currentStep -= 1;
		}
	};

	const handleBack = () => {
		prevStep();
	};

	const handleNext = () => {
		if (isLastStep) {
			handleComplete();
		} else {
			currentStep += 1;
		}
	};

	const handleDismiss = () => {
		showModal = false;
		currentStep = 0;
		dispatch('close');
	};

	const handleComplete = () => {
		showModal = false;
		currentStep = 0;
		dispatch('complete');
	};
</script>

<svelte:window bind:innerWidth={windowWidth} />

{#if $showNewLayout}
	<Modal hideTitle {showModal} on:close={handleDismiss}>
		<Container slot="content" padding="0">
			<div
				class="bg-color-base border-radius-m pb-m d-flex flex-direction-column align-items-center text-center"
				on:touchstart={handleTouchStart}
				on:touchend={handleTouchEnd}
			>
				<Text styleType="h1" class="mb-m text-white">
					{steps[currentStep].heading}
				</Text>

				<Text class="mb-m text-300 text-color-muted text-center width-full">
					{steps[currentStep].description}
				</Text>

				<div
					class="width-full max-width-full d-flex justify-content-center align-items-start mb-m"
				>
					<LottiePlayer
						src={steps[currentStep].lottie}
						width={lottieWidth}
						height={lottieHeight}
						showPreviewLabel={false}
						autoplay
						loop
					/>
				</div>

				<div class="d-flex align-items-center justify-content-center gap-s mb-m">
					{#each steps as _, i}
						<button
							class="step-dot"
							class:active={i === currentStep}
							on:click={() => (currentStep = i)}
							aria-label={$format('onboarding_step_indicator', {
								default: 'Step {step} of {total}',
								values: { step: i + 1, total: steps.length },
							})}
						/>
					{/each}
				</div>

				<div
					class="d-flex flex-direction-row flex-wrap align-items-center justify-content-center gap-s width-full mb-s"
				>
					<ButtonV2
						class="onboarding-btn mr-s"
						variant="secondary"
						disabled={isFirstStep}
						on:click={handleBack}
					>
						{$format('button_back', { default: 'Back' })}
					</ButtonV2>
					<ButtonV2 class="onboarding-btn" variant="primary" on:click={handleNext}>
						{isLastStep
							? $format('button_close', { default: 'Close' })
							: $format('button_next', { default: 'Next' })}
					</ButtonV2>
				</div>

				<p
					class="link-styles text-color-muted mt-s mb-0 cursor-pointer"
					role="presentation"
					on:click={handleDismiss}
				>
					{capFirstChar($format('ai_chat_popover_skip', { default: 'Skip' }))}
				</p>
			</div>
		</Container>
	</Modal>
{:else}
	<Modal
		{showModal}
		on:close={handleDismiss}
		fullHeight
		title={$format('homes_onboarding_wizard_title', {
			default: 'Multiple Homes & Home Sharing',
		})}
	>
		<svelte:fragment slot="content">
			<Wizard class="mb-m mt-xl">
				{#each steps as step, i (i)}
					<WizardItem
						on:click={() => goToStep(i)}
						active={currentStep === i}
						title={step.heading}
						disabled={false}
						showMoreDots={false}
						tooltipTitle={step.heading}
					/>
				{/each}
			</Wizard>

			<WizardContainer activeStepIndex={currentStep} adaptiveHeight fullHeight>
				{#each steps as step, i (i)}
					<WizardContent active={currentStep === i}>
						<Container fullHeight>
							<svelte:component
								this={step.component}
								description={step.description}
								src={step.lottie}
							/>
						</Container>
					</WizardContent>
				{/each}
			</WizardContainer>
		</svelte:fragment>

		<div slot="footer" class="d-flex justify-content-end">
			{#if !isFirstStep}
				<Button class="mr-s" variant="secondary" size="m" on:click={prevStep}>
					{$format('button_back', { default: 'Back' })}
				</Button>
			{/if}
			{#if !isLastStep}
				<Button variant="primary" size="m" on:click={nextStep}>
					{$format('button_next', { default: 'Next' })}
				</Button>
			{:else}
				<Button variant="primary" size="m" on:click={handleComplete}>
					{capFirstChar($format('button_close', { default: 'Close' }))}
				</Button>
			{/if}
		</div>
	</Modal>
{/if}

<style>
	.step-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		border: none;
		background-color: rgba(255, 255, 255, 0.3);
		padding: 0;
		cursor: pointer;
		transition: background-color 0.2s ease;
	}

	.step-dot.active {
		background-color: white;
	}

	.step-dot:hover {
		background-color: rgba(255, 255, 255, 0.6);
	}
</style>
